import type {
	ClientPortfolioCryptocurrencyInstructionResponseDTO,
	ClientPortfolioDetailBankInstructionResponseDTO,
	ClientPortfolioDetailBankProductToolResponseDTO,
	ClientPortfolioDetailBondResponseDTO,
	ClientPortfolioDetailBondToolResponseDTO,
	ClientPortfolioDetailCommodityResponseDTO,
	ClientPortfolioDetailCommodityToolResponseDTO,
	ClientPortfolioDetailCryptocurrencyToolResponseDTO,
	ClientPortfolioDetailFundToolResponseDTO,
	ClientPortfolioDetailInvestmentResponseDTO,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
	ClientPortfolioProducerInstrumentResponseDTO,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolReportingSummaryResponseDTO,
	ClientPortfolioToolTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

export const ToolKind = {
	Commodity: "commodity",
	Crypto: "crypto",
	Investment: "investment",
	Bond: "bond",
	BankProduct: "bankProduct",
} as const;

export type ToolKind = (typeof ToolKind)[keyof typeof ToolKind];

export const KINDS_WITH_PURCHASE_INSTRUCTIONS: readonly ToolKind[] = [
	ToolKind.Investment,
	ToolKind.Commodity,
	ToolKind.Crypto,
];

export type NormalizedInstruction<TRaw> = {
	id: number;
	startAt?: string | null;
	endAt?: string | null;
	frequency: ClientPortfolioInvestmentFrequencyEnum;
	investmentType: ClientPortfolioInvestmentTypeEnum | null;
	raw: TRaw;
};

type NormalizedToolBase = {
	id: number;
	identificationType?: ClientPortfolioToolIdentificationTypeEnum | null;
	identification?: string | null;
	name?: string | null;
	monthlyPayment: number;
};

export type NormalizedTool = NormalizedToolBase &
	(
		| {
				kind: typeof ToolKind.Investment;
				type?: ClientPortfolioToolTypeEnum | null;
				evaluation?: number | null;
				instructions: NormalizedInstruction<ClientPortfolioDetailInvestmentResponseDTO>[];
				raw: ClientPortfolioDetailFundToolResponseDTO;
		  }
		| {
				kind: typeof ToolKind.Bond;
				type?: null;
				evaluation?: number | null;
				instructions: NormalizedInstruction<ClientPortfolioDetailBondResponseDTO>[];
				raw: ClientPortfolioDetailBondToolResponseDTO;
		  }
		| {
				kind: typeof ToolKind.Commodity;
				type?: null;
				evaluation?: null;
				instructions: NormalizedInstruction<ClientPortfolioDetailCommodityResponseDTO>[];
				raw: ClientPortfolioDetailCommodityToolResponseDTO;
		  }
		| {
				kind: typeof ToolKind.Crypto;
				type?: null;
				evaluation?: null;
				instructions: NormalizedInstruction<ClientPortfolioCryptocurrencyInstructionResponseDTO>[];
				raw: ClientPortfolioDetailCryptocurrencyToolResponseDTO;
		  }
		| {
				kind: typeof ToolKind.BankProduct;
				type?: null;
				evaluation?: null;
				instructions: NormalizedInstruction<ClientPortfolioDetailBankInstructionResponseDTO>[];
				raw: ClientPortfolioDetailBankProductToolResponseDTO;
		  }
	);

export type NormalizedToolOfKind<K extends ToolKind> = Extract<
	NormalizedTool,
	{ kind: K }
>;

export type ToolRowCommonProps<K extends ToolKind = ToolKind> = {
	tool: NormalizedToolOfKind<K>;
	portfolioId: number;
	currencySign: string;
	toolsSummary?: ClientPortfolioToolReportingSummaryResponseDTO[];
	expanded: boolean;
	onToggle: (value: boolean) => void;
	noDivider: boolean;
};

export type ProducerInstrumentRowProps = {
	producerInstrument: ClientPortfolioProducerInstrumentResponseDTO;
	instrumentIdx: number;
	portfolioId: number;
	currencySign: string;
	expanded: boolean;
	onToggle: (value: boolean) => void;
	noDivider: boolean;
};
